import { useAuthRoles } from '_lib/hooks/useAuthRoles';

import { reportException } from '_lib/utils/sentryService';

import {
  CreateResource,
  DeleteResource,
  GetByIdResource,
  ListResource,
  PutHook,
  PutResource,
  ReactivateResource,
  ResourceId,
  UnpagedListHookResult,
  UnpagedListOptions,
  UnpagedListResource,
  UseCustomMutationOptions,
  createApiResource,
  useCustomMutation
} from '../apiResourceFactory';
import { API } from '../apiService';
import {
  UserListDTO,
  AccountUserQueryParams,
  IsActiveEnum,
  UserDetailDTO,
  AccountUserUpdateDTO,
  AccountUserDetailDTO,
  AccountUserCreateDTO,
  ChangePasswordDTO
} from '../apiTypes.shim';

import type { SetRequired } from 'type-fest';

type AllAccountUsersParams = SetRequired<Omit<AccountUserQueryParams, 'isActive'>, 'callCenterId' | 'accountId'>;

export interface AccountUserResource
  extends ListResource<UserListDTO, AccountUserQueryParams, { requireParams: true }>,
    UnpagedListResource<UserListDTO, AccountUserQueryParams>,
    GetByIdResource<AccountUserDetailDTO>,
    PutResource<AccountUserUpdateDTO, AccountUserDetailDTO>,
    CreateResource<AccountUserCreateDTO, AccountUserDetailDTO>,
    DeleteResource,
    ReactivateResource<UserDetailDTO> {
  useAllAccountUsersCached: (
    options: UnpagedListOptions<AllAccountUsersParams, { requireParams: true }>
  ) => UnpagedListHookResult<UserListDTO>;
  useChangePassword: PutHook<ChangePasswordDTO, string>;
}

export const accountUserResourceUri = `account-user`;

export const createAccountUserResource = (): AccountUserResource => {
  const {
    useUnpagedList,
    unpagedList,
    unpagedListQueryKey,
    list,
    useList,
    listQueryKey,
    getById,
    useGetById,
    getByIdQueryKey,
    create,
    useCreate,
    put,
    usePut,
    deleteItem,
    useDelete,
    reactivate,
    useReactivate
  } = createApiResource(API, accountUserResourceUri);

  type ChangePasswordVariables = { id: ResourceId; data: ChangePasswordDTO };
  const useChangePassword = (mutationConfig?: UseCustomMutationOptions<string, unknown, ChangePasswordVariables>) => {
    const context = useCustomMutation<string, unknown, ChangePasswordVariables>({
      mutationFn: async ({ id, data }) => {
        const response = await API.put(`${accountUserResourceUri}/${id}/change-password`, data);
        return typeof response.headers.getAuthorization === 'function' ? `${response.headers.getAuthorization()}` : '';
      },
      onError: (error) => reportException(error),
      ...mutationConfig
    });

    return { ...context, put: context.mutateAsync };
  };

  return {
    useAllAccountUsersCached: (options) => {
      const authRoles = useAuthRoles();

      return useUnpagedList(
        { ...options, params: { ...options.params, accountId: options.params.accountId, isActive: IsActiveEnum.Any } },
        {
          enabled: !authRoles.isCallCenterUser,
          refetchOnMount: false
        }
      );
    },
    useChangePassword,
    useUnpagedList,
    unpagedList,
    unpagedListQueryKey,
    list,
    useList,
    listQueryKey,
    getById,
    useGetById,
    getByIdQueryKey,
    create,
    useCreate,
    put,
    usePut,
    deleteItem,
    useDelete,
    reactivate,
    useReactivate
  };
};
